import { PolymerElement } from "@polymer/polymer/polymer-element.js";

import template from "./ns-action-menu.html";

/**
 * <p>
 * A dropdown intended for use with a list of actions, rather than a list of options.
 * It is intended to be used in conjunction with `<paper-item>`, as shown in the examples
 * below. Each item in the list is to be given an onclick callback function to perform the
 * action that item represents.
 * </p>
 *
 * <h2>Demo</h2>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <ns-action-menu>
 *       <paper-item onclick="doFirstAction()">Do An Action</paper-item>
 *       <paper-item onclick="doSecondAction()">Do Another Action</paper-item>
 *       <paper-item onclick="doThirdAction()">Do Something Else</paper-item>
 *     </ns-action-menu>
 *     <br/>
 *     <ns-action-menu disabled>
 *       <paper-item onclick="doFirstAction()">Do An Action</paper-item>
 *     </ns-action-menu>
 *     <ns-action-menu theme="secondary">
 *       <paper-item onclick="doFirstAction()">Do An Action</paper-item>
 *       <paper-item onclick="doSecondAction()">Do Another Action</paper-item>
 *     </ns-action-menu>
 *   </template>
 * </demo-snippet>
 *
 * <marked-element>
 *   <div slot="markdown-html">
 *     <script type="text/markdown">
 *          ### Attributes
 *          `<ns-action-menu>` provides the following attributes:
 *          `label`
 *          This attributes sets the label applied to the dropdown button.
 *
 *          `theme`
 *          This attribute selects the general styling theme to use with this component.
 *          Valid values are "primary", "primary-hit", "primary-inverse",
 *          "secondary", "secondary-inverse", "transparent, "transparent-hit",
 *          "transparent-inverse".
 *
 *          `disabled`
 *          This Boolean attribute indicates that the user cannot interact with this component.
 *          If this attribute is not set, the component is enabled.
 *
 *          `size`
 *          This attribute selects the vertical size of the component. A value of "short" sets the
 *          component to the Nu Skin short input height of 30px. If this attribute is not set,
 *          the component defaults to the Nu Skin standard input height of 45px.
 *
 *          `open`
 *          This attribute selects in which direction the option menu should open. A value of "up"
 *          sets the option menu to open upward, above the component. If this attribute is not set,
 *          the option menu defaults to open downward.
 *
 *          `opened`
 *          This read-only Boolean attribute indicates whether the option menu is currently open.
 *
 *          ### Styling
 *          `<ns-action-menu>` provides the following custom properties and mixins for styling:
 *          Property                    │ Description                                    │ Default Value
 *          ────────────────────────────┼────────────────────────────────────────────────┼──────────────
 *          `--font-button`             │ overrides styles applied to dropdown button    │ '{}'
 *          `--ns-action-menu-dropdown  │ overrides styles applied to dropdown section   │ '{}'
 *
 *     </script>
 *   </div>
 * </marked-element>
 *
 */
export class NsActionMenu extends PolymerElement {
  static get is() {
    return "ns-action-menu";
  }

  static get template() {
    let htmlElement = document.createElement("TEMPLATE");
    htmlElement.innerHTML = template;
    return htmlElement;
  }

  static get properties() {
    return {
      /**
       * Disables the component
       */
      disabled: {
        type: Boolean,
        value: false,
        reflectToAttribute: true
      },
      /**
       * Dropdown label
       */
      label: {
        type: String,
        value: ""
      },
      /**
       * Flag indicating whether the action list is opened
       */
      opened: {
        type: Boolean,
        value: false,
        reflectToAttribute: true,
        notify: true
      }
    };
  }

  /**
   * Opens the action list
   * @public
   */
  open() {
    if (!this.disabled) {
      this.$.dropdownContent.show();
      this.$.blurTarget.focus();
      this.opened = true;
    }
  }

  /**
   * Closes the action list
   * @public
   */
  close() {
    this.$.dropdownContent.hide();
    this.opened = false;
  }

  /**
   * Toggles the action list between opened and closed
   * @public
   */
  toggle() {
    if (!this.disabled && !this._blurring) {
      if (this.opened) this.close();
      else this.open();
    }
  }

  /**
   * Closes the dropdown on blur
   * @private
   */
  _onBlur() {
    this._blurring = true;
    setTimeout(() => {
      this.close();
      this.$.dropdownListbox.selected = -1;
      this._blurring = false;
    }, 150);
  }
}

customElements.define(NsActionMenu.is, NsActionMenu);
